import { useTranslation } from 'react-i18next'
import { ArrowBigDown, ArrowBigUp, MessageCircle } from 'lucide-react'
import type { CommunityVoteDirection } from '@/lib/communityPostData'

interface CommunityVoteBarProps {
  readonly viewerDirection: CommunityVoteDirection | null
  readonly netScore: number
  readonly commentCount: number
  readonly onPressUpvote: () => void
  readonly onPressDownvote: () => void
  readonly onPressComments?: () => void
  readonly commentsButtonDisabled?: boolean
  readonly layout?: 'inline' | 'sidebar'
}

export function CommunityVoteBar({
  viewerDirection,
  netScore,
  commentCount,
  onPressUpvote,
  onPressDownvote,
  onPressComments,
  commentsButtonDisabled = false,
  layout = 'inline',
}: CommunityVoteBarProps) {
  const { t } = useTranslation()
  const isUpvoted = viewerDirection === 'up'
  const isDownvoted = viewerDirection === 'down'

  const upvoteButton = (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onPressUpvote()
      }}
      aria-pressed={isUpvoted}
      aria-label={String(t('community.upvoteAria'))}
      className={`flex items-center justify-center rounded-md p-1.5 transition-colors ${
        isUpvoted ? 'text-primary' : 'text-foreground/60 hover:text-foreground'
      }`}
    >
      <ArrowBigUp className="w-5 h-5" fill={isUpvoted ? 'currentColor' : 'none'} />
    </button>
  )

  const scoreLabel = (
    <span
      className={`text-xs font-semibold tabular-nums ${
        isUpvoted ? 'text-primary' : isDownvoted ? 'text-muted-foreground' : 'text-foreground/80'
      }`}
    >
      {netScore}
    </span>
  )

  const downvoteButton = (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onPressDownvote()
      }}
      aria-pressed={isDownvoted}
      aria-label={String(t('community.downvoteAria'))}
      className={`flex items-center justify-center rounded-md p-1.5 transition-colors ${
        isDownvoted ? 'text-muted-foreground' : 'text-foreground/60 hover:text-foreground'
      }`}
    >
      <ArrowBigDown className="w-5 h-5" fill={isDownvoted ? 'currentColor' : 'none'} />
    </button>
  )

  const commentsButton = onPressComments ? (
    <button
      type="button"
      disabled={commentsButtonDisabled}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onPressComments()
      }}
      className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-foreground/70 transition-colors hover:text-foreground disabled:opacity-60 disabled:hover:text-foreground/70"
    >
      <MessageCircle className="w-4 h-4" />
      <span className="tabular-nums">{commentCount}</span>
    </button>
  ) : null

  if (layout === 'sidebar') {
    return (
      <div className="flex shrink-0 flex-col items-center gap-0.5">
        {upvoteButton}
        {scoreLabel}
        {downvoteButton}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <div className="inline-flex items-center rounded-full bg-secondary">
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onPressUpvote()
          }}
          aria-pressed={isUpvoted}
          aria-label={String(t('community.upvoteAria'))}
          className={`flex items-center gap-1 rounded-l-full px-2.5 py-1.5 transition-colors ${
            isUpvoted ? 'text-primary' : 'text-foreground/70 hover:text-foreground'
          }`}
        >
          <ArrowBigUp className="w-4 h-4" fill={isUpvoted ? 'currentColor' : 'none'} />
        </button>
        {scoreLabel}
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onPressDownvote()
          }}
          aria-pressed={isDownvoted}
          aria-label={String(t('community.downvoteAria'))}
          className={`flex items-center gap-1 rounded-r-full px-2.5 py-1.5 transition-colors ${
            isDownvoted ? 'text-muted-foreground' : 'text-foreground/70 hover:text-foreground'
          }`}
        >
          <ArrowBigDown className="w-4 h-4" fill={isDownvoted ? 'currentColor' : 'none'} />
        </button>
      </div>
      {commentsButton}
    </div>
  )
}
